import React, { useEffect, useState } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "./Dashboard.css";
import { useDispatch } from "react-redux";
import { onDashboardData } from "../../redux/homeAction";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Fetch dashboard data
  useEffect(() => {
    dispatch(onDashboardData(setLoading, selectedMonth, setDashboardData));
  }, [selectedMonth]);

  if (loading) return <p>Loading dashboard...</p>;
  if (!dashboardData) return <p>No data available</p>;

  const {
    kpi,
    salesChart,
    topProductsChart,
    yearlyTrendChart,
    revenueVsTargetChart,
    latestOrders,
  } = dashboardData;

  const lineOptions = {
    responsive: true,
    plugins: { legend: { display: true }, title: { display: false } },
    animation: { duration: 1000, easing: "easeOutQuart" },
    scales: { y: { beginAtZero: true } },
  };
  const barOptions = { ...lineOptions };

  return (
    <div className="dashboard-container">
      <h2 className="fw-bold mb-4">Overview</h2>

      {/* KPI Cards */}
      <div className="row mb-4">
        <div className="col-md-3 col-6 mb-3">
          <div className="card shadow-sm p-3 text-center">
            <h6>Total Orders</h6>
            <p className="fs-4 fw-bold text-primary">{kpi.totalOrders}</p>
          </div>
        </div>
        <div className="col-md-3 col-6 mb-3">
          <div className="card shadow-sm p-3 text-center">
            <h6>Revenue</h6>
            <p className="fs-4 fw-bold text-success">${kpi.totalRevenue}</p>
          </div>
        </div>
        <div className="col-md-3 col-6 mb-3">
          <div className="card shadow-sm p-3 text-center">
            <h6>Products</h6>
            <p className="fs-4 fw-bold text-warning">{kpi.totalProducts}</p>
          </div>
        </div>
        <div className="col-md-3 col-6 mb-3">
          <div className="card shadow-sm p-3 text-center">
            <h6>Customers</h6>
            <p className="fs-4 fw-bold text-info">{kpi.totalCustomers}</p>
          </div>
        </div>
      </div>

      {/* Sales Trend */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3">
          <div className="card shadow-sm p-3">
            <h6 className="fw-bold mb-3">Sales Trend (Full Year)</h6>
            <Line
              data={{
                labels: salesChart.labels,
                datasets: [
                  {
                    label: "Sales ($)",
                    data: salesChart.data,
                    fill: false,
                    borderColor: "rgba(75,192,192,1)",
                    backgroundColor: "rgba(75,192,192,0.2)",
                    tension: 0.3,
                  },
                ],
              }}
              options={lineOptions}
            />
          </div>
        </div>

        {/* Top Products Month Selector */}
        <div className="col-md-6 mb-3">
          <div className="card shadow-sm p-3">
            <h6 className="fw-bold mb-3">
              Top Products ({salesChart.labels[selectedMonth - 1]})
            </h6>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="form-select mb-3"
            >
              {salesChart.labels.map((month, i) => (
                <option key={i} value={i + 1}>
                  {month}
                </option>
              ))}
            </select>
            <Bar
              data={{
                labels: topProductsChart.labels,
                datasets: [
                  {
                    label: "Units Sold",
                    data: topProductsChart.data,
                    backgroundColor: "rgba(54, 162, 235, 0.6)",
                  },
                ],
              }}
              options={barOptions}
            />
          </div>
        </div>
      </div>

      {/* Top Products Yearly Trend */}
      <div className="row mb-4">
        <div className="col-12 mb-3">
          <div className="card shadow-sm p-3">
            <h6 className="fw-bold mb-3">Top Products Yearly Trend</h6>
            <Line
              data={yearlyTrendChart}
              options={{
                ...lineOptions,
                scales: { y: { beginAtZero: true, stepSize: 50 } },
              }}
            />
          </div>
        </div>
      </div>

      {/* Monthly Revenue vs Target */}
      <div className="row mb-4">
        <div className="col-12 mb-3">
          <div className="card shadow-sm p-3">
            <h6 className="fw-bold mb-3">Monthly Revenue vs Target</h6>
            <Bar
              data={revenueVsTargetChart}
              options={{
                responsive: true,
                plugins: { legend: { display: true } },
                animation: { duration: 1000, easing: "easeOutQuart" },
                scales: { y: { beginAtZero: true } },
              }}
            />
          </div>
        </div>
      </div>

      {/* Latest Orders Table */}
      <div className="card shadow-sm p-3">
        <h6 className="fw-bold mb-3">Latest Orders</h6>
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {latestOrders.map((order) => (
                <tr key={order.orderId}>
                  <td>{order.orderId}</td>
                  <td>{order.customer}</td>
                  <td>{order.product}</td>
                  <td>
                    <span
                      className={`badge ${
                        order.status === "delivered"
                          ? "bg-success"
                          : order.status === "pending"
                            ? "bg-warning"
                            : "bg-danger"
                      }`}
                    >
                      {order?.status?.charAt(0).toUpperCase() +
                        order?.status?.slice(1)}
                    </span>
                  </td>
                  <td>${order.amount}</td>
                  <td>
                    {new Date(order.date).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
